import type { Field, ItemFilter, ItemKind, ObjEntity } from "../api/types.gen.ts";
import { timeZone } from "./zone.ts";

/** Filters the items list takes straight from the page's URL. */
const PASSED = [
  "entity",
  "q",
  "issues",
  "kind",
  "attachments",
  "attached",
  "triage",
  "private_to",
  "visibility",
  "guests",
  "bookmarked",
  "since",
  "updated_since",
  "archived",
  "in_comments",
  "in_files",
  "thread",
] as const;

/**
 * The list's filters from the page's URL, as API query parameters (`where` conditions plus `extra` ones, and the
 * reference conditions, each repeatable).
 */
export function itemFilters(sp: URLSearchParams, extra: readonly string[] = []) {
  // `tz`: relative filter values ("today") mean the viewer's today.
  return {
    ...Object.fromEntries(PASSED.map((k) => [k, sp.get(k)])),
    where: [...sp.getAll("where"), ...extra],
    refers_to: sp.getAll("refers_to"),
    linked_from: sp.getAll("linked_from"),
    tz: timeZone.value,
  };
}

/** The filters that take true or false. */
const FLAGS: ReadonlySet<string> = new Set([
  "deep",
  "triage",
  "attachments",
  "attached",
  "guests",
  "archived",
  "bookmarked",
  "in_comments",
]);
/** What says which list it is (the screen picked in the sidebar), not how it's searched. */
const SCREEN: ReadonlySet<string> = new Set(["entity", "kind", "tz", "conversations", "folder", "deep"]);

/**
 * The list's filters from the page's URL as a bulk action's `filter` (every item the list matches): what the list
 * asks for, typed as the API's JSON takes it (in the folder `at` says, when it says one); and whether it's searched or
 * filtered beyond its screen.
 */
export function itemFilterBody(
  sp: URLSearchParams,
  conversations: boolean | null,
  at: { folder: string | null; deep: string | null } = { folder: null, deep: null },
): [ItemFilter, boolean] {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries({ ...itemFilters(sp), ...at })) {
    if (v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)) {
      out[k] = FLAGS.has(k) ? v === "true" : v;
    }
  }
  if (conversations) {
    out["conversations"] = true;
  }
  return [out as ItemFilter, Object.keys(out).some((k) => !SCREEN.has(k))];
}

/** An entity's choice fields: what a board can group by. */
export function choiceFields(entity: ObjEntity | undefined): Field[] {
  return (entity?.fields ?? []).filter((f) => f.type === "select");
}

/** The kinds of item, as filters offer them. */
export const KINDS: readonly ItemKind[] = ["email", "message", "webpage", "file", "record"];
